---
title: "Is the Reddit API free? What it costs and the limits"
description: "Reddit's API has a free tier, 100 queries a minute with OAuth, and a paid tier at $0.24 per 1,000 calls. Here's what's free, what's not, and the limits."
date: "2026-06-30"
author: "Kevin Wang"
tags: ["reddit", "api", "how-to"]
keyword: "is reddit api free"
ogImage: "/og/is-the-reddit-api-free.png"
---

export const setupSteps = [
  { label: "Reddit free tier", value: 4 },
  { label: "Reddit commercial", value: 6 },
  { label: "AnyAPI", value: 1 },
];

export const timeline = [
  { date: "Apr 18, 2023", title: "Reddit says it will start charging for the API" },
  { date: "May 31, 2023", title: "Apollo is quoted about $20M a year", highlight: true },
  { date: "Jun 1, 2023", title: "Price set at $0.24 per 1,000 calls", highlight: true },
  { date: "Jun 12, 2023", title: "7,000+ subreddits go dark in protest" },
  { date: "Jun 30, 2023", title: "Apollo and other apps shut down" },
  { date: "Jul 1, 2023", title: "New pricing takes effect" },
];

export const faqItems = [
  {
    q: "Is the Reddit API free?",
    a: "Yes, for non-commercial use under 100 queries a minute with OAuth (10 a minute without). Commercial use or higher volume is paid, at $0.24 per 1,000 calls. Since late 2025, even a free app needs Reddit's approval before it can call the API.",
  },
  {
    q: "How much does the Reddit API cost?",
    a: "The paid tier is $0.24 per 1,000 API calls. Reddit set that on June 1, 2023 and it's still the published rate in 2026. Below 100 queries a minute and non-commercial, it stays free.",
  },
  {
    q: "Does Reddit have an API?",
    a: "Yes. The official Reddit Data API returns posts, comments, subreddit listings, and search over OAuth. It's been free since 2008 for personal use and paid for commercial use since July 2023.",
  },
  {
    q: "What are the Reddit API rate limits?",
    a: "100 queries per minute per OAuth client, and 10 a minute without OAuth, averaged over a rolling ten-minute window so you can burst. Go past that and you need the paid plan.",
  },
  {
    q: "Can I get Reddit data without a Reddit API key?",
    a: "Yes. A hosted endpoint returns Reddit search, subreddit posts, and comments from one API key, with no Reddit app to register and nothing to get approved, priced per call in dollars.",
  },
];

<Callout type="tip" title="Key findings">

Yes, Reddit has an official API with a free tier: 100 queries a minute when you log in with OAuth, for non-commercial use. Go over that, or use it for anything commercial, and it's $0.24 per 1,000 calls on a paid plan you have to get approved for, and realistically that approval goes to large companies. Since late 2025, even the free tier needs Reddit to sign off on your app first. If you just want Reddit data without the OAuth app, the approval wait, or a contract, one call to a hosted endpoint returns posts, comments, and search results, priced in dollars per call.

</Callout>

You need Reddit data for something: monitoring what people say about your product, feeding threads into an AI model, or pulling leads out of subreddits.

Reddit has an official API for exactly this. **The catch is that you probably can't get it.**

Since late 2025 every app needs Reddit's approval before it works, and for anything commercial you basically don't get approved unless you're a large company. Plenty of developers submit and never hear back.

**This post covers what's free, what it costs, and how to get Reddit data without that approval.**

## Is the Reddit API free? The short answer

Reddit has an official Data API, and **yes, there's a free tier**. It's capped at 100 queries per minute when you authenticate with OAuth, and 10 a minute if you don't. That's plenty for a personal project, a mod bot, or a research script.

**It's not free for commercial use.** The moment you're building something that makes money, or you cross the rate limit, you're on the paid tier at $0.24 per 1,000 API calls.

Reddit set that rate on [June 1, 2023](https://www.techtarget.com/whatis/feature/Reddit-pricing-API-charge-explained) and it's still the published number in 2026. For what that rate adds up to at real volumes, eSEOspace has a full [Reddit API pricing breakdown](https://eseospace.com/blog/reddit-api-pricing/).

There's no monthly quota to track. The gate is the per-minute rate, measured over a rolling ten-minute window so you can burst a little.

Two catches most guides skip:

- **Everything real runs through OAuth.** The 10-a-minute unauthenticated path is barely usable, so you're registering an app either way.
- **Since late 2025, you need approval first.** Reddit's [Responsible Builder Policy](https://support.reddithelp.com/hc/en-us/articles/42728983564564-Responsible-Builder-Policy) now requires every app to be approved before it can hit the API, personal projects included. So "free" doesn't mean you can just sign up and start pulling data anymore.

The three ways to get Reddit data line up like this:

| | Free tier | Paid plan | AnyAPI |
| --- | --- | --- | --- |
| Auth | OAuth app | OAuth app | One API key |
| Reddit approval | Required (since late 2025) | Required, plus a contract | None |
| Rate limit | 100/min with OAuth | Negotiated | No per-key cap |
| Commercial use | Not allowed | Allowed | Allowed |
| Price | $0 | $0.24 / 1,000 calls | <Price sku="reddit.search" /> / 1,000 calls (no app, no approval) |
| Time to first call | Days, often never | Big companies only | Minutes |

Reddit's paid plan is the cheapest per call, if you can get the contract. The free tier costs nothing, as long as you fit inside it. The hosted endpoint is the one you can actually start using today. More on when to pick which below.

## What "free" actually means now, and its limits

The old tutorials get this wrong, so I'll be exact.

Free access is for non-commercial use only. Personal tools, mod bots, and research are fine.

A product that makes money is not, and Reddit reads "commercial" broadly: brand monitoring, competitor tracking, lead generation, and anything that resells or trains on the data all count.

For those you need approval and the paid agreement.

And as of late 2025, the approval step applies to everyone. Reddit's [Responsible Builder Policy](https://support.reddithelp.com/hc/en-us/articles/42728983564564-Responsible-Builder-Policy) made prior approval mandatory for all Data API access, hobby projects included, not just commercial use. You register the app, then wait for Reddit to clear it before your first call returns anything.

**That approval is where people get stuck.** Since the policy landed, developers have reported the create-app flow itself breaking. One hit a flat ["you cannot create any more applications"](https://github.com/mikf/gallery-dl/issues/8559) error trying to register a new app, and users of a revived Reddit client [couldn't get keys](https://github.com/Apollo-Reborn/Apollo-Reborn/issues/82) through the normal prefs/apps page anymore.

Apps that already had credentials keep working. New ones are the ones that stall.

One caveat: these are developer reports, not an official approval rate. Reddit doesn't publish how long review takes or what clears it. Commercial and AI use cases face the steepest barriers.

But if you're starting fresh today, approval is the step most likely to stop you, and it can sit with no answer.

This is all about the AI money. Reddit's signed data-licensing deals reportedly worth about [$60 million a year with Google](https://www.cjr.org/analysis/reddit-winning-ai-licensing-deals-openai-google-gemini-answers-rsl.php) and a second with OpenAI, part of a reported $200 million-plus in total commitments by late 2025.

So they didn't lock the API down on a whim. Reddit's data is what they sell now, and free scraping was eating into it.

My honest read: if you want official commercial access and you're not a big company, it's **effectively closed**. You have to clear approval, fill out a contact form, and sign a contract that fits Reddit's criteria.

Reddit's been busy licensing data to Google and OpenAI since 2023. They're not in a hurry to hand a startup a key.

A mod bot or a personal read-only script still gets through the free tier fine. A small company that wants Reddit data inside a product usually can't get official access at all.

## How to get a Reddit API key

If the official API is your path, this is the real sequence. None of the steps are hard. There's just more of them than the old guides let on.

1. Go to [reddit.com/prefs/apps](https://www.reddit.com/prefs/apps) and click "create app."
2. Pick a type. **Script** is the simplest and only touches your own account. **Web app** runs on a server and can keep a secret. **Installed** runs on a device you don't control and can't keep a secret, so it uses PKCE instead.
3. Reddit shows you a `client_id` under the app name, and a `client_secret` for script and web apps. Never share the secret.
4. Exchange those credentials for an OAuth token, then send your requests to `oauth.reddit.com`, not `www.reddit.com`.
5. Set a `User-Agent` in Reddit's required format: `platform:appid:version (by /u/username)`. Reddit throttles generic or missing User-Agents hard, so don't skip this.
6. Since late 2025, submit the app for approval and wait for the go-ahead. As I covered above, this approval is where new apps now stall, sometimes with no answer at all, so don't count on it clearing quickly.

<Figure src="/og/screenshots/reddit-create-app.png" alt="Reddit's app preferences page at reddit.com/prefs/apps, showing an authorized app's permissions above and the create-application form below with the web app, installed app, and script options" caption="The create-application form at reddit.com/prefs/apps. Pick script, web, or installed, and Reddit gives you a client ID and secret." width={2076} height={2166} />

## Why Reddit started charging: the 2023 timeline

For 15 years the API was free, and a whole ecosystem of apps grew on top of it. Then, in [April 2023](https://techcrunch.com/2023/04/18/reddit-will-begin-charging-for-access-to-its-api/), Reddit said it would start charging, pointing straight at the AI companies training on its data.

The prices landed six weeks later. Apollo, the most popular third-party Reddit app, got quoted around [$20 million a year](https://en.wikipedia.org/wiki/Reddit_API_controversy) and announced it would shut down. Thousands of subreddits went private in protest. Reddit held the line.

<Timeline items={timeline} caption="How Reddit's API went from free to paid in ten weeks of 2023." ariaLabel="Reddit API pricing timeline from April to July 2023." />

That's why the free tier looks the way it does now. Reddit figured its data was worth more to AI labs than to the developers building on it, so they set the price high enough to push everyone toward a contract.

## The one-call alternative

If your real problem is the OAuth app and the approval wait, this is what I use instead. One request returns Reddit search results, and [the same Reddit endpoints](/api/reddit) cover subreddit listings, thread comments, even a [Reddit video's transcript](/blog/video-transcript-api). **No app to register, nothing to get approved.**

The request below searches the top posts in r/homelab this month, and this is the real response it returned on September 27, 2026:

<CodeTabs labels={["cURL", "Python", "JavaScript"]}>

```bash
curl -X POST https://api.getanyapi.com/v1/run/reddit.search \
  -H "Authorization: Bearer $ANYAPI_KEY" \
  -H "Content-Type: application/json" \
  -d '{"query": "subreddit:homelab", "sort": "top", "timeframe": "month"}'
```

```python
import os, requests

res = requests.post(
    "https://api.getanyapi.com/v1/run/reddit.search",
    headers={"Authorization": f"Bearer {os.environ['ANYAPI_KEY']}"},
    json={"query": "subreddit:homelab", "sort": "top", "timeframe": "month"},
)
for post in res.json()["output"]["data"]["posts"]:
    print(post["score"], post["title"])
```

```js
const res = await fetch("https://api.getanyapi.com/v1/run/reddit.search", {
  method: "POST",
  headers: {
    Authorization: `Bearer ${process.env.ANYAPI_KEY}`,
    "Content-Type": "application/json",
  },
  body: JSON.stringify({ query: "subreddit:homelab", sort: "top", timeframe: "month" }),
});
const { output } = await res.json();
for (const post of output.data.posts) console.log(post.score, post.title);
```

</CodeTabs>

```json
{
  "output": {
    "found": true,
    "data": {
      "posts": [
        {
          "id": "1wlnt2q",
          "title": "I just wanted a home server… somehow this happened",
          "author": "IndependentProcess80",
          "score": 7869,
          "numComments": 468,
          "subreddit": "homelab",
          "permalink": "/r/homelab/comments/1wlnt2q/i_just_wanted_a_home_server_somehow_this_happened/",
          "url": "https://www.reddit.com/r/homelab/comments/1wlnt2q/i_just_wanted_a_home_server_somehow_this_happened/",
          "createdUtc": 1789926793
        },
        {
          "id": "1we0sqa",
          "title": "The county landfill has a separate bin for electronics and I’ve taken to raiding it once a week to scavenge for parts for my labs.",
          "author": "tsuto",
          "score": 7497,
          "numComments": 446,
          "subreddit": "homelab",
          "permalink": "/r/homelab/comments/1we0sqa/the_county_landfill_has_a_separate_bin_for/",
          "url": "https://www.reddit.com/gallery/1we0sqa",
          "createdUtc": 1789181894
        }
      ],
      "nextCursor": "cur_vjeHzUmD2fBlSS5qg7zk0Q"
    }
  },
  "provider": "AnyAPI",
  "costUsd": 0.00038
}
```

You need one thing to run it: an API key, which a new account gets for free. There's no OAuth app, no `client_secret`, and no approval queue.

Each post comes back with the fields you'd actually use: the `title`, `author`, `score`, `numComments`, and `subreddit`, plus a `permalink` to the thread and the post `id`.

The `query` accepts Reddit's own operators, so `subreddit:homelab`, `author:`, and boolean `AND`/`OR`/`NOT` all work inside the string.

When there are more results, `nextCursor` carries the next page.

That call cost $0.00038 on September 27, 2026, the `costUsd` you see right in the response, billed in dollars with no subscription. {/* receipt 2026-09-27 */}

The published price is <Price sku="reddit.search" /> per 1,000 requests. It's more per call than Reddit's own $0.24 per 1,000. I'll get to when the extra is worth paying below.

## Which path should you use?

So which of the three do you use? Depends on what you're building.

- **Personal project on your own account, low volume.** Reddit's free tier is the right call. Register a script app, get it approved, and stay under 100 queries a minute. It costs nothing but your setup time, and for a hobby tool that time is cheap.
- **A large company with the volume to justify a contract.** Go direct to Reddit's paid plan. At $0.24 per 1,000 calls nothing beats it, once you've built the integration and gotten the contract signed. Getting there is the catch. That contract realistically goes to companies big enough for Reddit to want at the table, and if that's you, doing millions of calls a month, the per-call price is the only number that matters.
- **A commercial product you want running now, without the app or the contract.** Pay per call. Yes, <Price sku="reddit.search" /> per 1,000 is about 1.6 times Reddit's rate. The extra buys you out of the whole setup: no OAuth app, no waiting on approval, no rate-limit babysitting, no negotiating commercial access. You get the same response shape as [200-plus other data sources](/catalog) too. For low or moderate volume it's worth it, and it saves me a week I'd rather spend elsewhere. The gap only adds up at real scale: a million calls a month is <Price sku="reddit.search" calls={1000000} /> here against $240 on Reddit's paid plan. If you're doing that volume and Reddit will sign the contract, go direct.

The setup gap is the part that doesn't show up in the per-call price:

<Chart caption="Steps before your first Reddit call. Fewer is better. The free tier still needs an OAuth app plus Reddit's approval; commercial access adds a contract on top of that.">
  <BarChart data={setupSteps} showValues valueSuffix="" unit="steps" baseline={0} height={230} format={(n) => `${n}`} ariaLabel="Steps before a first Reddit API call: Reddit free tier 4, Reddit commercial 6, AnyAPI 1." />
</Chart>

I've set up the official API more than once. It's fine when you own the account and you're not doing much volume, same as I'd say for [scraping TikTok](/blog/how-to-scrape-tiktok).

The hosted call is what I grab when the thing has to be commercial and it has to work today.

<FAQ items={faqItems} />

## Related guides

<RelatedGuides items={[
  { to: "/blog/how-to-scrape-tiktok", title: "How to scrape TikTok", blurb: "Every TikTok endpoint, one API call each, on live data." },
  { to: "/blog/scrapingbee-vs-scraperapi", title: "ScrapingBee vs ScraperAPI", blurb: "I ran 96 requests on both. Same success rate, very different speed and price." },
  { to: "/blog/video-transcript-api", title: "Video transcript API", blurb: "One call for transcripts across TikTok, YouTube, and Reddit video." },
]} />

<CTA to="/catalog" note="Reddit search, subreddit posts, and comments, plus 200-plus other data sources, one key, priced in dollars. New accounts start with free credit.">
  Browse the data catalog
</CTA>
